import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { translations } from "@/i18n/translations";

const LanguageContext = createContext(null);

function readInitialLang() {
  if (typeof window === "undefined") return "en";
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("lang");
    if (fromQuery === "en" || fromQuery === "ar") return fromQuery;
    const fromStorage = localStorage.getItem("flowwhite_lang");
    if (fromStorage === "en" || fromStorage === "ar") return fromStorage;
  } catch (e) {
    // localStorage / URL parsing can throw in restricted contexts; fall through.
  }
  return "en";
}

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(readInitialLang);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");

    try {
      localStorage.setItem("flowwhite_lang", lang);
      const url = new URL(window.location.href);
      if (url.searchParams.get("lang") !== lang) {
        url.searchParams.set("lang", lang);
        window.history.replaceState({}, "", url);
      }
    } catch (e) {
      // Ignore — same restricted-context guard as above.
    }

    const meta = translations[lang]?.meta;
    if (!meta) return;

    document.title = meta.title;
    setMeta('meta[name="description"]', "content", meta.description);
    setMeta('meta[property="og:title"]', "content", meta.title);
    setMeta('meta[property="og:description"]', "content", meta.description);
    setMeta('meta[property="og:locale"]', "content", lang === "ar" ? "ar_QA" : "en_US");
    setMeta('meta[name="twitter:title"]', "content", meta.title);
    setMeta('meta[name="twitter:description"]', "content", meta.description);
  }, [lang]);

  const value = useMemo(() => {
    const t = translations[lang];
    const toggle = () => setLang((l) => (l === "en" ? "ar" : "en"));
    return { lang, t, setLang, toggle, isRtl: lang === "ar" };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
}
